import React from 'react';

/**
 * What an element renderer needs from the section placing it: the base its
 * derived test ids hang off (the section's `testId ?? id`, so route tests keep
 * finding `<base>-timeline`, `<base>-evidence`, `<base>-table`), the
 * band contrast for primitives that paint their own surface, and the heading
 * level a copy heading takes inside the section.
 */
export interface FmElementContextValue {
  base: string;
  contrast: 'light' | 'dark';
  headingLevel: 2 | 3 | 4;
  /**
   * A heading an earlier sibling section in the same chapter already printed.
   * A copy heading equal to it renders titleless, like one equal to the chapter title.
   */
  printedHeading?: string;
}

const FmElementContext = React.createContext<FmElementContextValue | null>(null);

export const FmElementContextProvider = FmElementContext.Provider;

/** Elements render only inside a section; a bare element has no base to derive test ids from. */
export function useElementContext(): FmElementContextValue {
  const context = React.useContext(FmElementContext);
  if (!context) {
    throw new Error('An element renderer must be placed by FmSection, which supplies its base id.');
  }
  return context;
}

/** An element's own test id wins; otherwise the suffix hangs off the section base. */
export function derivedTestId(
  context: FmElementContextValue,
  own: string | undefined,
  suffix: string,
): string {
  return own ?? `${context.base}-${suffix}`;
}
